<template>
    <jet-authentication-card :card-size="'xl'">
        <template #logo>
            <jet-authentication-card-logo />

            <h1
                class="font-semibold text-iron-900 mt-[26px] text-center text-[26px] tracking-[-0.1px]"
            >
                {{ $t("onboarding.segmentation.title") }}
            </h1>
            <p
                class="max-w-md text-sm font-medium text-iron-500 mx-auto text-center"
            >
                {{ $t("onboarding.segmentation.description") }}
            </p>
        </template>

        <div class="gap-4 flex flex-col">
            <button
                type="button"
                :disabled="form.processing"
                :class="tileClass('link')"
                @click="select('link')"
            >
                <div
                    class="rounded-lg bg-emerald-50 h-12 w-12 flex flex-shrink-0 items-center justify-center"
                >
                    <Link2 :size="22" class="text-emerald-600" />
                </div>
                <div class="flex flex-1 flex-col">
                    <span
                        class="text-base font-semibold text-iron-900 tracking-[-0.01em]"
                    >
                        {{ $t("onboarding.segmentation.link.title") }}
                    </span>
                    <span class="text-sm text-iron-500 tracking-[-0.01em]">
                        {{ $t("onboarding.segmentation.link.description") }}
                    </span>
                </div>
                <ArrowRight
                    :size="18"
                    class="mt-1 text-iron-400 flex-shrink-0"
                />
            </button>

            <button
                type="button"
                :disabled="form.processing"
                :class="tileClass('embed')"
                @click="select('embed')"
            >
                <div
                    class="rounded-lg bg-violet-50 h-12 w-12 flex flex-shrink-0 items-center justify-center"
                >
                    <Code2 :size="22" class="text-violet-600" />
                </div>
                <div class="flex flex-1 flex-col">
                    <span
                        class="text-base font-semibold text-iron-900 tracking-[-0.01em]"
                    >
                        {{ $t("onboarding.segmentation.embed.title") }}
                    </span>
                    <span class="text-sm text-iron-500 tracking-[-0.01em]">
                        {{ $t("onboarding.segmentation.embed.description") }}
                    </span>
                </div>
                <ArrowRight
                    :size="18"
                    class="mt-1 text-iron-400 flex-shrink-0"
                />
            </button>
        </div>

        <p class="mt-4 text-xs text-iron-400 text-center">
            {{ $t("onboarding.segmentation.footnote") }}
        </p>
    </jet-authentication-card>
</template>

<script lang="ts" setup>
import JetAuthenticationCard from "@/Jetstream/AuthenticationCard.vue";
import JetAuthenticationCardLogo from "@/Jetstream/AuthenticationCardLogo.vue";
import { useAnalytics } from "@/composables/useAnalytics";
import { useForm } from "@inertiajs/vue3";
import { ArrowRight, Code2, Link2 } from "lucide-vue-next";
import { onMounted } from "vue";
import route from "ziggy-js";

type Persona = "link" | "embed";

const analytics = useAnalytics();
const segmentationOpenedAt = Date.now();

const form = useForm<{ persona: Persona | null }>({
    persona: null,
});

onMounted(() => {
    analytics.trackEvent("onboarding_segmentation_viewed");
});

const select = (persona: Persona) => {
    if (form.processing) {
        return;
    }
    form.persona = persona;

    analytics.trackEvent("onboarding_segmentation_persona_selected", {
        persona,
        time_to_decide_ms: Date.now() - segmentationOpenedAt,
    });

    form.post(route("app.onboard.finish"), {
        preserveScroll: true,
        onSuccess: () => {
            analytics.trackEvent("onboarding_segmentation_completed", {
                persona,
                time_to_decide_ms: Date.now() - segmentationOpenedAt,
            });
        },
        onError: (errors) => {
            analytics.trackEvent("onboarding_segmentation_failed", {
                persona,
                errors: Object.keys(errors),
            });
        },
    });
};

const tileClass = (persona: Persona) => [
    "gap-4 rounded-xl border-iron-200 bg-white p-5 text-left flex items-start border transition-all hover:border-z-primary-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-z-primary-300",
    form.processing && form.persona === persona
        ? "border-z-primary-400 ring-2 ring-z-primary-200"
        : "",
    form.processing ? "cursor-wait" : "cursor-pointer",
];
</script>
